import { formatCents, tt } from '@agos/shared'

import { basketProgress, outsideNote, type ZoneOutcome } from './delivery'

/**
 * Why *Záväzne objednať* will not send - `FRONTEND.md` §9.417.
 *
 * **The report this exists to answer.** The owner saw the button do nothing at 7,90 €, found that
 * 10,90 € worked, and concluded from the two amounts that a zone minimum was following them onto
 * *osobný odber* (FB-745). The refusal at 7,90 was correct: the basket was under the zone's minimum
 * and `maySubmit` said so. What it never did was **say so to the person pressing it**, so the only
 * evidence available was the amount, and a guess off two amounts is what got reported.
 *
 * **`maySubmit` is seven terms and the button already states two of them** - *Reštaurácia je
 * zatvorená* when the restaurant is shut, *Odosielame…* while the request is in flight. Those two
 * are the pattern; this finishes it for the other five rather than inventing anything.
 *
 * **Why this is not `maySubmit` itself.** The page still decides whether to send; this decides what
 * to say. Keeping them separate is what lets the property test below assert that the two agree,
 * which is the only assertion that can catch a *sixth* silent reason being added later.
 */
export interface SubmitBlock {
  /**
   * What `data-submit-block` carries and what the tests pin.
   *
   * Separate from the sentence because a spec asserting Slovak prose breaks on a wording change,
   * and §9.385's lesson was that the sentence has to stay free to improve.
   */
  code: string
  /** What the customer reads, beside the button. */
  message: string
}

/**
 * The field problems, in the order a customer should be told about them.
 *
 * **Not `submitProblems`' push order.** That function appends in the order its checks happen to be
 * written; this is the order the form is filled in, which is the order somebody fixing it works
 * through. The codes are its codes, so a new problem there shows up here as an unhandled code
 * rather than as silence - see `fieldMessage`.
 */
const FIELD_ORDER = ['name', 'phone', 'address', 'payment-method', 'scheduled-at'] as const

function fieldMessage(code: string): string {
  switch (code) {
    case 'name':
      return tt('Doplňte meno.')
    case 'phone':
      return tt('Doplňte telefónne číslo.')
    case 'address':
      return tt('Doplňte adresu doručenia.')
    case 'payment-method':
      return tt('Vyberte spôsob platby.')
    case 'scheduled-at':
      return tt('Vyberte dátum aj čas predobjednávky.')
    default:
      // A code `submitProblems` grew and nobody listed here. Saying *something* is the whole point
      // of this module, so an unknown problem gets the generic sentence rather than `null` - which
      // would be the silence this entry exists to remove.
      return tt('Skontrolujte prosím vyplnené údaje.')
  }
}

export interface SubmitBlockInput {
  /** `submitProblems(facts, priced)` - six codes, none of which had ever reached the screen. */
  problems: readonly string[]
  /** `preorderProblem`'s own sentence, or `''`. Passed through, never restated. */
  scheduling: string
  /** `deliveryFor(needsAddress ? point : null, …)` - already gated on the channel needing an address. */
  delivery: ZoneOutcome
  /** `channel.requiresDeliveryAddress`. */
  needsAddress: boolean
  /** The goods, before delivery and discounts - what the zone minimum is measured against. */
  subtotalCents: number
}

/**
 * The one reason to show, or `null` when the order can be sent.
 *
 * <b>One reason at a time, and it must be the one that can be acted on first.</b> The basket comes
 * before the form: being under the zone minimum is the only reason here that is *not* fixed on this
 * screen - it is fixed by going back to the menu - so sending somebody through name, phone and
 * payment first is the wrong order, and it is the order a naive list of checks produces.
 *
 * <b>Two states are deliberately absent.</b> `closed` and *sending* each already own the whole
 * button label, so there is nothing for a second sentence to add; `submit-block.test.ts` names them
 * as excluded from its completeness property rather than leaving the gap to be rediscovered.
 */
export function submitBlock(input: SubmitBlockInput): SubmitBlock | null {
  const { problems, scheduling, delivery, needsAddress, subtotalCents } = input

  if (problems.includes('empty-basket')) {
    return { code: 'empty-basket', message: tt('Košík je prázdny.') }
  }

  if (delivery.kind === 'below-minimum') {
    // **The shortfall comes from `basketProgress`, not from a second subtraction here.** The note in
    // the address card is drawn from the same call, so the two places on this page that name what
    // the basket is missing cannot disagree - which is the fault §10A.132's note warns about
    // between a client and a server, and is worse inside one screen.
    const progress = basketProgress(delivery, subtotalCents)
    const missing = progress !== null && progress.kind === 'minimum' ? progress.missingCents : 0
    const minimum = `${tt('Minimálna objednávka pre túto oblasť je')} ${formatCents(delivery.minimumCents)}.`
    return {
      code: 'below-minimum',
      // Legacy's toast named the minimum alone. *Chýba ešte* is the half somebody acts on, and the
      // figure already existed - it was being drawn 600 lines above the button.
      message: missing > 0 ? `${minimum} ${tt('Chýba ešte')} ${formatCents(missing)}.` : minimum,
    }
  }

  for (const code of FIELD_ORDER) {
    if (problems.includes(code)) return { code, message: fieldMessage(code) }
  }

  // Anything `submitProblems` grows that this file has not been taught. Ordered after the known
  // fields so the sentences above keep their precedence, and before the zone cases so a form
  // problem is never hidden behind an address one.
  const unlisted = problems.find(
    (code) => code !== 'empty-basket' && !FIELD_ORDER.includes(code as (typeof FIELD_ORDER)[number]),
  )
  if (unlisted !== undefined) return { code: unlisted, message: fieldMessage(unlisted) }

  if (scheduling !== '') return { code: 'scheduling', message: scheduling }

  if (needsAddress) {
    if (delivery.kind === 'outside') {
      // The same sentence `deliveryNote` gives, and deliberately the same: two wordings for one
      // refusal on one screen reads as two different problems.
      return { code: 'outside', message: outsideNote(delivery) }
    }
    if (delivery.kind === 'unknown') {
      return {
        code: 'address-unknown',
        message: tt('Zadajte adresu doručenia, aby sme vedeli cenu dopravy.'),
      }
    }
  }

  return null
}
